// AccionesMasivasDialog.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    LinearProgress,
    Chip,
    IconButton,
    Paper,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Divider,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import {
    Close as CloseIcon,
    Description as DescriptionIcon,
    Assignment as AssignmentIcon,
    Print as PrintIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Settings as SettingsIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Delete as DeleteIcon,
    FilterList as FilterIcon,
    Gavel as GavelIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    Bookmark as BookmarkIcon,
    AttachFile as AttachFileIcon,
    Folder as FolderIcon,
    ExpandMore as ExpandMoreIcon,
    Star as StarIcon,
    Save as SaveIcon
} from '@mui/icons-material';

import { usePermisos } from '../../../utils/usePermisos';
import {
    tiposMovimientosJWT,
    escritosListarJWT,
    escritoGetJWT,
    plantillasListarJWT,
    crearMovimientoJWT,
    userInfoJWT,
} from '../../../api/endpoints';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import logoSIGIF from '../../../assets/sigifblue.png';
import PlantillasReportesManager from './PlantillasReportesManager';
import TinyEditor from '../../TinyEditor';

// ==================== HELPERS DE NORMALIZACIÓN ====================
const normKey = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
const keyify = (s) => normKey(s).replace(/\s+/g, "");
const isTipo = (dbTipo, ...aliases) => aliases.some(a => keyify(a) === keyify(dbTipo));

// === Helper para obtener el "login" del usuario actual, soportando varias formas ===
const getUsuarioLogin = (rawUsuario) => {
    if (!rawUsuario) return '';

    // Si te llega un string directo (ej: "ELI")
    if (typeof rawUsuario === 'string') {
        return rawUsuario.trim();
    }

    // Función interna para probar campos típicos en un objeto
    const tryObject = (u) => {
        if (!u || typeof u !== 'object') return '';
        const keys = [
            'Usuario',
            'usuario',
            'Login',
            'login',
            'NombreUsuario',
            'nombreUsuario',
            'nombre'
        ];
        for (const k of keys) {
            if (u[k]) return String(u[k]).trim();
        }
        return '';
    };

    // 1) Probar el objeto tal cual
    let login = tryObject(rawUsuario);
    if (login) return login;

    // 2) Si tiene propiedad "usuario", probar adentro
    if (rawUsuario.usuario) {
        login = tryObject(rawUsuario.usuario);
        if (login) return login;
    }

    // 3) Fallback: nada encontrado
    return '';
};

// Helper para mostrar label amigable del tipo
const displayTipoLabel = (tipo) => {
    if (!tipo) return '';
    // Capitalizar primera letra de cada palabra
    return String(tipo).toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

// Helper para verificar si un tipo requiere escrito
const tipoReqEscrito = (tipo) => {
    if (!tipo) return false;
    return isTipo(tipo, 'PROCESAL', 'EXTRA PROCESAL', 'MIS ESCRITOS', 'RECIBOS');
};

// Convertir número a texto en español (para CAPITAL_RECLAMADO_LETRAS)
function numeroALetras(n) {
    if (n == null || isNaN(n) || n === 0) return '';
    const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
        'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte'];
    const DECENAS = ['', '', 'veinti', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
    function seccion(num) {
        if (num === 0) return '';
        if (num <= 20) return UNIDADES[num];
        if (num < 30) return 'veinti' + UNIDADES[num - 20];
        if (num < 100) { const d = Math.floor(num / 10), u = num % 10; return DECENAS[d] + (u ? ' y ' + UNIDADES[u] : ''); }
        if (num === 100) return 'cien';
        if (num < 1000) { const c = Math.floor(num / 100), resto = num % 100; return CENTENAS[c] + (resto ? ' ' + seccion(resto) : ''); }
        if (num < 2000) return 'mil ' + seccion(num - 1000);
        if (num < 1000000) { const miles = Math.floor(num / 1000), resto = num % 1000; return seccion(miles) + ' mil' + (resto ? ' ' + seccion(resto) : ''); }
        if (num < 2000000) return 'un millón' + (num % 1000000 ? ' ' + seccion(num % 1000000) : '');
        const millones = Math.floor(num / 1000000), resto = num % 1000000;
        return seccion(millones) + ' millones' + (resto ? ' ' + seccion(resto) : '');
    }
    const valor = Math.abs(parseFloat(n));
    const entero = Math.floor(valor);
    const centavos = Math.round((valor - entero) * 100);
    let texto = seccion(entero);
    if (centavos > 0) texto += ' con ' + seccion(centavos) + '/100';
    else texto += ' con 00/100';
    return texto.charAt(0).toUpperCase() + texto.slice(1);
}


// Campos disponibles para EXPEDIENTES ADMINISTRATIVOS
// (no incluye conceptos puramente judiciales como caratula, tipo/objeto/etapa
// procesal, contraparte/abogados, ni movs PROCESAL / ESTADO PROCESO)
const CAMPOS_ADMINISTRATIVOS = {
    'Identificación': [
        { id: 'NumeroExpediente', label: 'N° Expediente', ancho: 120 },
        { id: 'Extracto', label: 'Extracto', ancho: 200 }
    ],
    'Estado': [
        { id: 'Estado', label: 'Estado', ancho: 100 }
    ],
    'Partes': [
        { id: 'Iniciador', label: 'Iniciador', ancho: 150 }
    ],
    'Módulo / Repartición': [
        { id: 'Modulo', label: 'Área Origen', ancho: 180 },
        { id: 'Reparticion', label: 'Repartición', ancho: 150 }
    ],
    'Fechas': [
        { id: 'FechaInicio', label: 'Fecha Inicio', ancho: 90 },
        { id: 'FechaIngreso', label: 'Fecha Ingreso', ancho: 90 },
        { id: 'FechaAgregado', label: 'Fecha Agregado a Lista', ancho: 90 }
    ],
    'Movimientos': [
        { id: 'UltimoMovimiento', label: 'Último Movimiento', ancho: 180 },
        { id: 'UltimoMovimientoTipo', label: 'Tipo Últ. Mov.', ancho: 110 },
        { id: 'FechaMovimiento', label: 'Fecha Últ. Mov.', ancho: 90 },
        { id: 'UltimoExtraProcesal', label: 'Últ. Extra Procesal', ancho: 180 },
        { id: 'FechaUltimoExtraProcesal', label: 'Fecha Extra Procesal', ancho: 90 },
        { id: 'UltimoOtroMov', label: 'Últ. Otro Mov.', ancho: 180 },
        { id: 'FechaUltimoOtroMov', label: 'Fecha Otro Mov.', ancho: 90 },
        { id: 'UltimaReferencia', label: 'Últ. Referencia', ancho: 180 },
        { id: 'FechaUltimaReferencia', label: 'Fecha Referencia', ancho: 90 }
    ],
    'Otros': [
        { id: 'Notas', label: 'Notas de la Lista', ancho: 200 }
    ]
};

// Campos disponibles para EXPEDIENTES JUDICIALES

// Todos los campos disponibles organizados por categoría
const CAMPOS_DISPONIBLES_COMPLETO = {
    'Identificación': [
        { id: 'Caratula', label: 'Carátula', ancho: 180 },
        { id: 'Autos', label: 'Autos/Expediente', ancho: 80 },
        { id: 'Autos2Instancia', label: 'Autos 2da Instancia', ancho: 80 }
    ],
    'Clasificación': [
        { id: 'Estado', label: 'Estado', ancho: 70 },
        { id: 'TipoProceso', label: 'Tipo de Proceso', ancho: 80 },
        { id: 'ObjetoReclamo', label: 'Objeto Reclamo', ancho: 100 },
        { id: 'EtapaProcesal', label: 'Etapa Procesal', ancho: 80 }
    ],
    'Radicación': [
        { id: 'Radicacion', label: 'Radicación Completa', ancho: 200 },
        { id: 'Juzgado', label: 'Juzgado', ancho: 80 },
        { id: 'Fuero', label: 'Fuero', ancho: 80 },
        { id: 'Jurisdiccion', label: 'Jurisdicción', ancho: 120 }
    ],
    'Fechas': [
        { id: 'FechaInicio', label: 'Fecha Inicio', ancho: 65 },
        { id: 'FechaAsignacion', label: 'Fecha Asignación', ancho: 65 },
        { id: 'FechaRadicacion', label: 'Fecha Radicación', ancho: 65 },
        { id: 'FechaAgregado', label: 'Fecha Agregado', ancho: 65 },
        { id: 'FechaMovimiento', label: 'Fecha Últ. Mov.', ancho: 65 }
    ],
    'Partes': [
        { id: 'Abogados', label: 'Abogado(s)', ancho: 120 },
        { id: 'Actor', label: 'Parte Actora', ancho: 100 },
        { id: 'Reparticion', label: 'Repartición', ancho: 120 },
        { id: 'Demandado', label: 'Demandado', ancho: 120 },
        { id: 'CUIT', label: 'CUIT', ancho: 70 },
        { id: 'DireccionDemandado', label: 'Dirección Demandado', ancho: 150 }
    ],
    'Certificados': [
        { id: 'Serie', label: 'Serie', ancho: 50 },
        { id: 'Certificado', label: 'Certificado', ancho: 70 },
        { id: 'Impuesto', label: 'Impuesto', ancho: 80 },
        { id: 'Cuenta', label: 'Cuenta', ancho: 70 }
    ],
    'Montos': [
        { id: 'MontoCapital', label: 'Capital', ancho: 70 },
        { id: 'MontoInteres', label: 'Interés', ancho: 70 },
        { id: 'MontoTotal', label: 'Monto Total', ancho: 80 }
    ],
    'Plan de Pago': [
    ],
    'Movimientos': [
        { id: 'UltimoMovimiento', label: 'Último Movimiento', ancho: 150 },
        { id: 'UltimoMovimientoTipo', label: 'Tipo Últ. Mov.', ancho: 110 },
        { id: 'FechaMovimiento', label: 'Fecha Últ. Mov.', ancho: 65 },
        { id: 'UltimoEstadoProceso', label: 'Últ. Estado Proceso', ancho: 180 },
        { id: 'FechaUltimoEstadoProceso', label: 'Fecha Estado Proceso', ancho: 65 },
        { id: 'UltimoProcesal', label: 'Últ. Procesal', ancho: 180 },
        { id: 'FechaUltimoProcesal', label: 'Fecha Procesal', ancho: 65 },
        { id: 'UltimoExtraProcesal', label: 'Últ. Extra Procesal', ancho: 180 },
        { id: 'FechaUltimoExtraProcesal', label: 'Fecha Extra Procesal', ancho: 65 },
        { id: 'UltimaReferencia', label: 'Últ. Referencia', ancho: 180 },
        { id: 'FechaUltimaReferencia', label: 'Fecha Referencia', ancho: 65 }
    ],
    'Otros': [
        { id: 'CartaPoder', label: 'Carta Poder', ancho: 60 },
        { id: 'Prioridad', label: 'Prioridad', ancho: 60 }
    ]
};

const TODOS_LOS_CAMPOS = Object.values(CAMPOS_DISPONIBLES_COMPLETO).flat();

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        minHeight: '85vh',
        maxHeight: '90vh'
    },
    header: {
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        padding: theme.spacing(2, 3)
    },
    content: {
        padding: theme.spacing(3),
        backgroundColor: '#fafafa'
    },
    accionCard: {
        padding: theme.spacing(3),
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'all 0.3s',
        border: '2px solid transparent',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
        }
    },
    accionCardSelected: {
        borderColor: '#1976d2',
        backgroundColor: '#e3f2fd'
    },
    accionIcon: {
        fontSize: 56,
        marginBottom: theme.spacing(2)
    },
    progressItem: {
        padding: theme.spacing(1),
        borderRadius: 4,
        marginBottom: theme.spacing(1),
        backgroundColor: 'white',
        border: '1px solid #e0e0e0'
    },
    progressSuccess: {
        borderLeftColor: '#4caf50',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid'
    },
    progressError: {
        borderLeftColor: '#f44336',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid'
    },
    progressPending: {
        borderLeftColor: '#ff9800',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid'
    },
    statsCard: {
        padding: theme.spacing(2),
        borderRadius: 4,
        textAlign: 'center'
    },
    campoItem: {
        padding: theme.spacing(0.75),
        marginBottom: theme.spacing(0.5),
        borderRadius: 4,
        backgroundColor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        fontSize: 13
    },
    campoItemSelected: {
        backgroundColor: '#e3f2fd',
        borderColor: '#1976d2'
    },
    grupoHeader: {
        fontWeight: 600,
        color: '#1976d2',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        fontSize: 11
    },
    filterBox: {
        padding: theme.spacing(1.5),
        backgroundColor: '#fff3e0',
        borderRadius: 4,
        marginBottom: theme.spacing(2)
    }
}));

// Constantes
const ACCIONES = [
    {
        id: 'movimiento',
        titulo: 'Insertar Movimientos',
        descripcion: 'Agregar movimientos con escritos personalizados a todos los expedientes',
        icon: DescriptionIcon,
        color: '#1976d2'
    },
    {
        id: 'reporte',
        titulo: 'Generar Reporte',
        descripcion: 'Crear un reporte consolidado de los expedientes',
        icon: PrintIcon,
        color: '#00796b'
    }
];

const PASOS = ['Seleccionar acción', 'Configurar', 'Ejecutar'];

// Helpers
const todayLocalISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Función auxiliar para parsear fechas .NET
const parsearFecha = (fecha) => {
    if (!fecha) return null;

    // Si es string con formato /Date(timestamp)/
    if (typeof fecha === 'string') {
        const match = fecha.match(/\/Date\((-?\d+)([+-]\d{4})?\)\//);
        if (match) {
            return new Date(parseInt(match[1], 10));
        }
        // Intentar parseo directo
        const parsed = new Date(fecha);
        if (!isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    // Si ya es Date
    if (fecha instanceof Date && !isNaN(fecha.getTime())) {
        return fecha;
    }

    return null;
};

// Formatear fecha a string
const formatearFecha = (fecha) => {
    const parsed = parsearFecha(fecha);
    if (!parsed) return '-';
    return parsed.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Obtener valor de campo de un expediente
const getValorCampo = (expediente, campoId) => {
    const mapeo = {
        // ===== CAMPOS ADMINISTRATIVOS =====
        NumeroExpediente: expediente.Expediente_Adm || expediente.Autos || expediente.Nro_Expediente || '-',
        Extracto: expediente.Extracto || expediente.Caratula || '-',
        Iniciador: expediente.Iniciador || '-',
        Modulo: expediente.Modulo || '-',  // ✅ Este viene del JOIN con Modulos
        FechaIngreso: formatearFecha(expediente.Fecha_Inicio || expediente.Fecha_Ingreso),

        // ===== CAMPOS JUDICIALES =====
        // Identificación
        Caratula: expediente.Caratula || expediente.caratula || '-',
        Autos: expediente.Autos || expediente.autos || '-',
        Autos2Instancia: expediente.Autos_2_Instancia || '-',

        // Clasificación
        Estado: expediente.Estado || expediente.estado || 'Sin estado',
        TipoProceso: expediente.Tipo_Proceso || '-',
        ObjetoReclamo: expediente.Objeto_Reclamo || '-',
        EtapaProcesal: expediente.Etapa_Procesal || '-',

        // Radicación
        Radicacion: [expediente.Juzgado, expediente.Fuero, expediente.Jurisdiccion].filter(Boolean).join(' | ') || '-',
        Juzgado: expediente.Juzgado || '-',
        Fuero: expediente.Fuero || '-',
        Jurisdiccion: expediente.Jurisdiccion || '-',

        // Fechas
        FechaInicio: formatearFecha(expediente.Fecha_Inicio),
        FechaAsignacion: formatearFecha(expediente.Fecha_Asignacion),
        FechaRadicacion: formatearFecha(expediente.Fecha_Radicacion),
        FechaAgregado: formatearFecha(expediente.Fecha_Agregado),
        FechaMovimiento: formatearFecha(expediente.Fecha_Movimiento),

        // Partes
        Abogados: expediente.Abogados_Participantes || expediente.Abogados || '-',
        Abogado: expediente.Abogados_Participantes || expediente.Abogados || '-', // alias singular para plantillas globales
        // Actor: lo dejamos con Iniciador como backup por si no hay parte cargada
        Actor: expediente.Actor || '-',
        Reparticion: expediente.Reparticion_Origen || expediente.Reparticion || '-',
        // Demandado ahora viene bien desde SQL, no hace falta caer a Extracto
        Demandado: expediente.Demandado || '-',
        CUIT: expediente.CUIT || expediente.Demandado_DNI || '-',
        DireccionDemandado: expediente.Demandado_Direccion || expediente.Direccion_Demandado || '-',

        // Certificados
        Serie: expediente.Serie || '-',
        Certificado: expediente.Certificado || '-',
        Impuesto: expediente.Impuesto || '-',
        Cuenta: expediente.Cuenta || '-',

        // Montos
        // 👇 Capital = TOTAL (como dijiste)
        MontoCapital: expediente.Monto_Capital != null ? `$${Number(expediente.Monto_Capital).toLocaleString('es-AR')}` : '-',
        MontoInteres: expediente.Monto_Interes != null ? `$${Number(expediente.Monto_Interes).toLocaleString('es-AR')}` : '-',
        // MontoTotal: usa Capital_Reclamado (calculado en backend) con fallback a Monto_Total
        MontoTotal: expediente.Capital_Reclamado != null ? `$${Number(expediente.Capital_Reclamado).toLocaleString('es-AR')}` : (expediente.Monto_Total != null ? `$${Number(expediente.Monto_Total).toLocaleString('es-AR')}` : '-'),

        // Plan de Pago
        PlanPagoNumero: expediente.Plan_Pago_Numero || '-',
        PlanPagoEstado: expediente.Plan_Pago_Estado || '-',

        // Movimientos
        UltimoMovimiento: expediente.Ultimo_Movimiento || '-',
        UltimoMovimientoTipo: expediente.Ultimo_Movimiento_Tipo || '-',
        // Últimos por tipo (utiles para reportes de exp admin)
        UltimoEstadoProceso: expediente.Ultimo_EstadoProceso || '-',
        FechaUltimoEstadoProceso: formatearFecha(expediente.Fecha_Ultimo_EstadoProceso),
        UltimoProcesal: expediente.Ultimo_Procesal || '-',
        FechaUltimoProcesal: formatearFecha(expediente.Fecha_Ultimo_Procesal),
        UltimoExtraProcesal: expediente.Ultimo_ExtraProcesal || '-',
        FechaUltimoExtraProcesal: formatearFecha(expediente.Fecha_Ultimo_ExtraProcesal),
        UltimoOtroMov: expediente.Ultimo_OtroMov || '-',
        FechaUltimoOtroMov: formatearFecha(expediente.Fecha_Ultimo_OtroMov),
        UltimaReferencia: expediente.Ultima_Referencia || '-',
        FechaUltimaReferencia: formatearFecha(expediente.Fecha_Ultima_Referencia),

        // Otros
        CartaPoder: expediente.CartaPoder || expediente.Carta_Poder || '-',
        Notas: expediente.Notas || '-',
        Observaciones: expediente.Extracto || expediente.Tramite || '-',
        Prioridad: expediente.Visto ? 'Visto' : 'Pendiente'
    };

    return mapeo[campoId] ?? '-';
};


// Obtener ancho de columna (respeta tipo de expediente — antes solo miraba
// CAMPOS_DISPONIBLES_COMPLETO y los campos exclusivos de admin caian al default).
const getAnchoColumna = (campoId, tipoExpediente) => {
    if (tipoExpediente != null) {
        const camposDisponibles = getCamposDisponibles(tipoExpediente);
        const todos = Object.values(camposDisponibles).flat();
        const campoTipado = todos.find(c => c.id === campoId);
        if (campoTipado) return campoTipado.ancho || 80;
    }
    const campo = TODOS_LOS_CAMPOS.find(c => c.id === campoId);
    return campo?.ancho || 80;
};

// Obtener label de campo
const getLabelCampo = (campoId, tipoExp) => {
    const camposDisponibles = getCamposDisponibles(tipoExp);
    const todosCampos = Object.values(camposDisponibles).flat();
    const campo = todosCampos.find(c => c.id === campoId);
    return campo?.label || campoId;
};

// Obtener valor crudo para ordenamiento
const getValorCrudo = (expediente, campoId) => {
    const mapeo = {
        'Caratula': expediente.Caratula || expediente.caratula || '',
        'Autos': expediente.Autos || expediente.autos || expediente.Nro_Expediente || '',
        'Estado': expediente.Estado || expediente.estado || '',
        'FechaInicio': expediente.Fecha_Inicio || '',
        'UltimoMovimiento': expediente.Ultimo_Movimiento || '',
        'Abogado': expediente.Abogado || expediente.abogado || '',
        'Juzgado': expediente.Juzgado || expediente.juzgado || '',
        'MontoTotal': expediente.Capital_Reclamado || expediente.Monto_Total || 0
    };
    return mapeo[campoId] !== undefined ? mapeo[campoId] : (expediente[campoId] || '');
};

// Helper para obtener campos según tipo de expediente
const getCamposDisponibles = (tipoExpediente) => {
    if (tipoExpediente === 2) {
        // Administrativo
        return CAMPOS_ADMINISTRATIVOS;
    }
    // Judicial (tipo 1 o default)
    return CAMPOS_DISPONIBLES_COMPLETO;
};

const getTodosCampos = (tipoExpediente) => {
    const campos = getCamposDisponibles(tipoExpediente);
    return Object.values(campos).flat();
};

// Wrapper para TinyEditor que maneja el montaje/desmontaje limpio
const TinyEditorWrapper = React.memo(({ htmlEscrito, setHtmlEscrito }) => {
    const [ready, setReady] = useState(false);
    const idRef = useRef(`tiny-masivo-${Date.now()}`);

    useEffect(() => {
        const currentId = idRef.current; // Capturar el valor actual
        const timer = setTimeout(() => setReady(true), 150);
        return () => {
            clearTimeout(timer);
            if (window.tinymce?.get?.(currentId)) {
                try {
                    window.tinymce.remove(`#${currentId}`);
                } catch (e) { /* ignore cleanup errors */ }
            }
        };
    }, []);

    if (!ready) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" style={{ marginLeft: 8 }}>
                    Preparando editor...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                <TinyEditor
                    id={idRef.current}
                    initialHtml={htmlEscrito}
                    onChange={setHtmlEscrito}
                />
            </Box>

            {/* Info de variables */}
            <Paper style={{
                padding: '12px 16px',
                backgroundColor: '#fff3e0',
                marginTop: 16,
                borderLeft: '4px solid #ff9800'
            }}>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>Variables disponibles:</strong> Las variables entre llaves serán reemplazadas automáticamente con los datos de cada expediente al ejecutar.
                    <Box mt={1} style={{ fontSize: 11, fontFamily: 'monospace', color: '#666' }}>
                        {'{Caratula}'} {'{Demandado}'} {'{Actor}'} {'{Autos}'} {'{Juzgado}'} {'{Radicacion}'} {'{Abogados}'} {'{CUIT}'} {'{Monto_Total}'} {'{Fecha_Movimiento}'} {'{Estado}'} {'{Certificado}'} {'{Serie}'} ...
                    </Box>
                </Typography>
            </Paper>
        </>
    );
});

const AccionesMasivasDialog = ({
    open,
    onClose,
    expedientes = [],
    tipoExpediente = 1,
    usuario,
    onSuccess,
    lista = null
}) => {
    const classes = useStyles();

    // 👉 NUEVO: login ya resuelto del usuario actual
    const [usuarioLogin, setUsuarioLogin] = useState('');

    useEffect(() => {
        // 1) Intentar con la prop
        let login = getUsuarioLogin(usuario);

        // 2) Si no hay nada, intentar con window.SIGIF?.usuario
        if (!login && typeof window !== 'undefined' && window.SIGIF?.usuario) {
            login = getUsuarioLogin(window.SIGIF.usuario);
        }

        // 3) Fallback: si sigue vacío, pegarle a user-info JWT una sola vez
        if (!login) {
            (async () => {
                try {
                    const res = await userInfoJWT();
                    if (res?.ok && res.usuario) {
                        const login2 = getUsuarioLogin(res.usuario);
                        if (login2) {
                            setUsuarioLogin(login2);
                            return;
                        }
                    }
                } catch (e) { /* ignore */ }
            })();
        } else {
            setUsuarioLogin(login);
        }
    }, [usuario]);

    // Estado principal
    const [pasoActual, setPasoActual] = useState(0);
    const [accionSeleccionada, setAccionSeleccionada] = useState(null);

    const permisos = usePermisos();

    const [plantillaPredefinida, setPlantillaPredefinida] = useState('personalizado');
    const [, setPlantillaGuardada] = useState(null);

    // Datos de configuración
    const [tiposMov, setTiposMov] = useState([]);
    const [escritos, setEscritos] = useState([]);
    const [plantillas, setPlantillas] = useState([]);
    const [loading, setLoading] = useState(false);

    // Formulario movimiento
    const [movimientoData, setMovimientoData] = useState({
        fecha: todayLocalISO(),
        tipo: '',
        descripcion: '',
        escrito: ''
    });

    // Estados para el nuevo sistema de movimientos
    const [htmlEscrito, setHtmlEscrito] = useState('');
    const [cargandoEscrito, setCargandoEscrito] = useState(false);
    const [loadingEscritos] = useState(false);

    // Estado para adjuntos PDF (Cédula)
    const [archivoPDF, setArchivoPDF] = useState(null);

    // Configuración de reporte
    const [reporteConfig, setReporteConfig] = useState({
        titulo: lista?.Nombre || 'Listado de Expedientes',
        descripcion: lista?.Descripcion || '',
        incluirDescripcion: false,
        campos: tipoExpediente === 2
            ? ['NumeroExpediente', 'Iniciador', 'FechaInicio']
            : ['Caratula', 'Autos', 'Estado'],
        agrupacion: '',
        incluirResumen: true,
        incluirGrafico: true,
        orientacion: 'landscape',
        colorearEstados: true,
        soloGraficos: false,
        graficosSeleccionados: ['estado', 'juzgado', 'abogado', 'montos', 'temporal']
    });

    const [plantillasManagerOpen, setPlantillasManagerOpen] = useState(false);
    const [plantillasAbrirEditor, setPlantillasAbrirEditor] = useState(false);
    const [, setReporteTab] = useState(0);
    const [modoPersonalizado, setModoPersonalizado] = useState(false);
    const [reporteTabView, setReporteTabView] = useState(0); // 0=Públicas, 1=Mis reportes

    // Ordenamiento y filtros
    const [ordenarPor, setOrdenarPor] = useState('');
    const [ordenDireccion, setOrdenDireccion] = useState('asc');
    const [filtroEstado, setFiltroEstado] = useState('');

    // Vista previa
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    // Ejecución
    const [ejecutando, setEjecutando] = useState(false);
    const [progreso, setProgreso] = useState([]);
    const [resumen, setResumen] = useState({ exitosos: 0, errores: 0, total: 0 });

    // Cancelación
    const canceladoRef = useRef(false);

    // Obtener estados únicos para el filtro
    const estadosUnicos = useMemo(() => {
        const estados = new Set();
        expedientes.forEach(exp => {
            const estado = exp.Estado || exp.estado || 'Sin estado';
            estados.add(estado);
        });
        return Array.from(estados).sort();
    }, [expedientes]);

    // Expedientes procesados (filtrados y ordenados) - usa completos para reportes si disponibles
    const expedientesProcesados = useMemo(() => {
        let resultado = [...expedientes];

        // Filtrar por estado
        if (filtroEstado) {
            resultado = resultado.filter(exp => {
                const estado = exp.Estado || exp.estado || 'Sin estado';
                return estado === filtroEstado;
            });
        }

        // Ordenar
        if (ordenarPor) {
            resultado.sort((a, b) => {
                let valA = getValorCrudo(a, ordenarPor);
                let valB = getValorCrudo(b, ordenarPor);

                // Manejar fechas
                if (ordenarPor.includes('Fecha') || ordenarPor.includes('Movimiento')) {
                    valA = valA ? new Date(valA).getTime() : 0;
                    valB = valB ? new Date(valB).getTime() : 0;
                }
                // Manejar números
                else if (ordenarPor.includes('Monto')) {
                    valA = Number(valA) || 0;
                    valB = Number(valB) || 0;
                }
                // Strings
                else {
                    valA = String(valA).toLowerCase();
                    valB = String(valB).toLowerCase();
                }

                if (valA < valB) return ordenDireccion === 'asc' ? -1 : 1;
                if (valA > valB) return ordenDireccion === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return resultado;
    }, [expedientes, filtroEstado, ordenarPor, ordenDireccion]);

    // Filtrar plantillas por tipo expediente (Judiciales / Administrativos)
    const categoriaActual = tipoExpediente === 2 ? 'Administrativos' : 'Judiciales';

    const plantillasGlobales = useMemo(() => {
        return plantillas.filter(pl => {
            const scope = (pl.Scope || pl.scope || '').toUpperCase();
            if (scope !== 'GLOBAL') return false;
            const cat = (pl.Categoria || pl.categoria || '').toLowerCase();
            return cat === categoriaActual.toLowerCase() || cat === 'ambos';
        });
    }, [plantillas, categoriaActual]);

    const plantillasUsuario = useMemo(() => {
        return plantillas.filter(pl => {
            const scope = (pl.Scope || pl.scope || '').toUpperCase();
            if (scope === 'GLOBAL') return false;
            const cat = (pl.Categoria || pl.categoria || '').toLowerCase();
            return !cat || cat === categoriaActual.toLowerCase() || cat === 'ambos';
        });
    }, [plantillas, categoriaActual]);

    // Cargar HTML del escrito cuando se selecciona uno (SIN reemplazar variables)
    useEffect(() => {
        if (!movimientoData.escrito) {
            setHtmlEscrito('');
            return;
        }

        let cancelado = false;

        const cargarPlantilla = async () => {
            setCargandoEscrito(true);
            setHtmlEscrito(''); // Limpiar primero

            try {
                // Buscar el escrito seleccionado en el array de escritos
                const escritoObj = escritos.find(e =>
                    (e.Id || e.id) === movimientoData.escrito
                );

                if (!escritoObj) {
                    setCargandoEscrito(false);
                    return;
                }

                // Obtener el contenido/HTML del escrito (plantilla cruda con variables)
                let htmlPlantilla = escritoObj.Contenido || escritoObj.contenido ||
                    escritoObj.Html || escritoObj.html ||
                    escritoObj.Plantilla || escritoObj.plantilla || '';

                // Si no tiene contenido en el objeto, intentar obtenerlo de la API
                if (!htmlPlantilla) {
                    const escritoId = escritoObj.Id || escritoObj.id;
                    try {
                        const data = await escritoGetJWT(escritoId);
                        htmlPlantilla = data?.Contenido || data?.contenido ||
                            data?.Html || data?.html ||
                            data?.Plantilla || data?.plantilla || '';
                    } catch (err) { /* ignore */ }
                }

                // Solo setear si no se canceló
                if (!cancelado) {
                    // Pequeño delay para asegurar que TinyMCE esté listo
                    await new Promise(resolve => setTimeout(resolve, 100));
                    setHtmlEscrito(htmlPlantilla);
                }

            } catch (error) {
                console.error('Error cargando escrito:', error);
                if (!cancelado) {
                    setHtmlEscrito('');
                }
            } finally {
                if (!cancelado) {
                    setCargandoEscrito(false);
                }
            }
        };

        cargarPlantilla();

        // Cleanup para evitar race conditions
        return () => {
            cancelado = true;
        };
    }, [movimientoData.escrito, escritos]);

    // Cargar datos iniciales
    useEffect(() => {
        if (open) {
            cargarDatosIniciales();
            // Reset estado
            setPasoActual(0);
            setAccionSeleccionada(null);
            setMovimientoData({
                fecha: todayLocalISO(),
                tipo: '',
                descripcion: '',
                escrito: ''
            });
            setReporteConfig({
                titulo: lista?.Nombre || 'Listado de Expedientes',
                descripcion: lista?.Descripcion || '',
                incluirDescripcion: false,
                campos: tipoExpediente === 2
                    ? ['NumeroExpediente', 'Iniciador', 'FechaInicio']
                    : ['Caratula', 'Autos', 'Estado'],
                agrupacion: '',
                incluirResumen: true,
                incluirGrafico: true,
                orientacion: 'landscape',
                colorearEstados: true,
                soloGraficos: false
            });
            setPlantillaPredefinida('personalizado');
            setPlantillaGuardada(null);
            setReporteTab(0);
            setModoPersonalizado(false);
            setReporteTabView(0);
            setOrdenarPor('');
            setOrdenDireccion('asc');
            setFiltroEstado('');
            setProgreso([]);
            setResumen({ exitosos: 0, errores: 0, total: 0 });
            canceladoRef.current = false;
            setHtmlEscrito('');
            setCargandoEscrito(false);
        }
    }, [open, lista, tipoExpediente]);

    const cargarDatosIniciales = async () => {
        setLoading(true);
        try {
            const [tiposRes, escritosRes, plantillasRes] = await Promise.all([
                tiposMovimientosJWT(),
                escritosListarJWT(),
                plantillasListarJWT('todos')
            ]);

            setTiposMov(tiposRes?.tipos || []);
            setEscritos((escritosRes?.escritos || []).filter(e => {
                const activo = e?.activo ?? e?.Activo;
                return activo === true || activo === 1 || activo === '1';
            }));
            setPlantillas(plantillasRes?.items || []);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper para obtener el nombre del tipo de forma segura
    const getTipoNombre = (tipo) => {
        return tipo?.Tipo_Movimiento || tipo?.Nombre || tipo?.nombre || tipo?.tipo || '';
    };

    // Helper para verificar tipos especiales
    const esCedula = (tipo) => isTipo(tipo, 'CEDULA');

    // Filtrar tipos de movimiento para el menú (replica lógica de MovimientosTab)
    const tiposParaMenu = useMemo(() => {
        if (!tiposMov || tiposMov.length === 0) return [];

        const esJudicial = tipoExpediente === 1;

        // Función helper local
        const buscarTipo = (nombre) => tiposMov.find(t => isTipo(getTipoNombre(t), nombre));

        const resultado = [];

        if (esJudicial) {
            // === JUDICIAL ===

            // 1. Cédula
            const cedula = buscarTipo('CEDULA');
            if (cedula) resultado.push({ ...cedula, icon: 'email' });

            // 3. Referencia
            const referencia = buscarTipo('REFERENCIA');
            if (referencia) resultado.push({ ...referencia, icon: 'link' });

            // Separador
            if (resultado.length > 0) {
                resultado.push({ isDivider: true, label: '── Escritos ──' });
            }

            // 4. Procesal (tipo real de BD)
            const procesal = buscarTipo('PROCESAL');
            if (procesal) resultado.push({ ...procesal, icon: 'description' });

            // 5. Extra Procesal (tipo real de BD)
            const extraProcesal = buscarTipo('EXTRA PROCESAL');
            if (extraProcesal) resultado.push({ ...extraProcesal, icon: 'receipt' });

            // Separador
            resultado.push({ isDivider: true, label: '── Personales ──' });

            // 6. Mis Escritos (virtual - no existe en BD)
            resultado.push({
                Id: 'virtual_mis',
                id: 'virtual_mis',
                Nombre: 'MIS ESCRITOS',
                nombre: 'MIS ESCRITOS',
                Tipo_Movimiento: 'MIS ESCRITOS',
                isVirtual: true,
                icon: 'person'
            });

        } else {
            // === ADMINISTRATIVO ===

            // Tipos administrativos
            const otroMov = buscarTipo('OTRO MOV');
            if (otroMov) resultado.push({ ...otroMov, icon: 'edit' });

            const referenciaAdm = buscarTipo('REFERENCIA');
            if (referenciaAdm) resultado.push({ ...referenciaAdm, icon: 'link' });

            const ofigen = buscarTipo('OFIGEN');
            if (ofigen) resultado.push({ ...ofigen, icon: 'description' });

            const oficinaPagos = buscarTipo('OFICINA DE PAGOS');
            if (oficinaPagos) resultado.push({ ...oficinaPagos, icon: 'receipt' });

            // Separador
            if (resultado.length > 0) {
                resultado.push({ isDivider: true, label: '── Recibos ──' });
            }

            // Recibos (virtual para filtrar)
            resultado.push({
                Id: 'virtual_recibos',
                id: 'virtual_recibos',
                Nombre: 'RECIBOS',
                nombre: 'RECIBOS',
                Tipo_Movimiento: 'RECIBOS',
                isVirtual: true,
                icon: 'receipt'
            });

            // Separador
            resultado.push({ isDivider: true, label: '── Personales ──' });

            // Mis Escritos
            resultado.push({
                Id: 'virtual_mis',
                id: 'virtual_mis',
                Nombre: 'MIS ESCRITOS',
                nombre: 'MIS ESCRITOS',
                Tipo_Movimiento: 'MIS ESCRITOS',
                isVirtual: true,
                icon: 'person'
            });
        }

        return resultado;
    }, [tiposMov, tipoExpediente]);

    // Escritos filtrados según el tipo de movimiento seleccionado
    const escritosFiltrados = useMemo(() => {
        if (!escritos.length || !movimientoData.tipo) return [];

        const tipoNombre = movimientoData.tipo;

        // Filtrar escritos activos
        const activos = escritos.filter(e => {
            const activo = e?.activo ?? e?.Activo ?? e?.active;
            return activo === true || activo === 1 || activo === '1';
        });

        // Helper para verificar si está publicado y es público
        const esPublicoPublicado = (e) => {
            const scope = (e.scope || e.Scope || '').toUpperCase();
            const estado = (e.estado || e.Estado || '').toUpperCase();
            const esPrivado = scope === 'PRIVATE' || scope === 'PRIVADO';
            const esPublicado = estado === 'PUBLICADO' || estado === 'PUBLISHED' || estado === 'ACTIVO';
            return esPublicado && !esPrivado;
        };

        // 1. MIS ESCRITOS: publicados, privados y del usuario actual
        if (isTipo(tipoNombre, 'MIS ESCRITOS')) {
            const usuarioActual = usuarioLogin;

            return activos.filter(e => {
                const estado = (e?.estado || e?.Estado || '').toUpperCase().trim();
                const scope = (e?.scope || e?.Scope || '').toUpperCase().trim();
                const owner = (e?.ownerUsuario || e?.OwnerUsuario || e?.owner || e?.Usuario || e?.usuario || '').toString().trim();

                // Publicado / Activo (por las dudas)
                const esPublicado = ['PUBLICADO', 'PUBLISHED', 'ACTIVO'].includes(estado);

                // Privado (MIS ESCRITOS)
                const esPrivado = scope === 'PRIVATE' || scope === 'PRIVADO';

                // Es mío: si hay owner, debe coincidir; si no viene owner, lo dejamos pasar por ahora
                const esMio = owner
                    ? (owner.toUpperCase() === usuarioActual.toUpperCase())
                    : true; // fallback: si aún no manejás owner en BD, que no filtre por esto

                return esPublicado && esPrivado && esMio;
            });
        }


        // 2. CEDULA: Solo escritos de cédula
        if (isTipo(tipoNombre, 'CEDULA')) {
            return activos.filter(e => {
                const nombre = (e.Nombre || e.nombre || e.escrito || e.Escrito || '').toLowerCase();
                const categoria = (e.categoria || e.Categoria || '').toLowerCase();
                const esCedula = /cedula|cédula/i.test(nombre) || /cedula|cédula/i.test(categoria);
                return esCedula && esPublicoPublicado(e);
            });
        }

        // 3. PROCESAL: Todo menos recibos, cédulas, carta poder, readjudicación y privados
        if (isTipo(tipoNombre, 'PROCESAL')) {
            return activos.filter(e => {
                const categoria = (e.categoria || e.Categoria || '').toLowerCase();
                const nombre = (e.Nombre || e.nombre || e.escrito || e.Escrito || '').toLowerCase();
                const esReciboCedula = /recibo|cedula|cédula/i.test(categoria) || /recibo|cedula|cédula/i.test(nombre);
                const esCartaPoder = /carta[\s_-]*poder/i.test(nombre) || /carta[\s_-]*poder/i.test(categoria);
                const esReadjudicacion = categoria.includes('readjudicaci');
                return !esReciboCedula && !esCartaPoder && !esReadjudicacion && esPublicoPublicado(e);
            });
        }

        // 5. EXTRA PROCESAL (jud): Solo categoría "Extra procesal / Recibos"
        if (isTipo(tipoNombre, 'EXTRA PROCESAL')) {
            return activos.filter(e => {
                const categoria = (e.categoria || e.Categoria || '').toLowerCase();
                return categoria.includes('extra procesal') && esPublicoPublicado(e);
            });
        }

        // 5b. RECIBOS (admin): Solo categoría "Recibo administrativo"
        if (isTipo(tipoNombre, 'RECIBOS')) {
            return activos.filter(e => {
                const categoria = (e.categoria || e.Categoria || '').toLowerCase();
                return categoria.includes('recibo administrativo') && esPublicoPublicado(e);
            });
        }

        // 6. OTRO MOV, OFIGEN, OFICINA DE PAGOS (admin): Todo menos recibos/cédulas/carta poder/readjudicación
        if (isTipo(tipoNombre, 'OTRO MOV', 'OFIGEN', 'OFICINA DE PAGOS')) {
            return activos.filter(e => {
                const categoria = (e.categoria || e.Categoria || '').toLowerCase();
                const nombre = (e.Nombre || e.nombre || e.escrito || e.Escrito || '').toLowerCase();
                const esReciboCedula = categoria.includes('recibo') || categoria.includes('cedula') || categoria.includes('cédula');
                const esCartaPoder = /carta[\s_-]*poder/i.test(nombre) || /carta[\s_-]*poder/i.test(categoria);
                const esReadjudicacion = categoria.includes('readjudicaci');
                return !esReciboCedula && !esCartaPoder && !esReadjudicacion && esPublicoPublicado(e);
            });
        }

        // 7. REFERENCIA: Todos los escritos públicos
        if (isTipo(tipoNombre, 'REFERENCIA')) {
            return activos.filter(e => esPublicoPublicado(e));
        }

        // 8. Default: Todos los públicos publicados
        return activos.filter(e => esPublicoPublicado(e));

    }, [escritos, movimientoData.tipo, usuarioLogin]);

    // Validar si puede avanzar al siguiente paso
    const puedeAvanzar = () => {
        if (pasoActual === 0) {
            return accionSeleccionada !== null;
        }

        if (pasoActual === 1) {
            if (accionSeleccionada === 'movimiento') {
                if (!movimientoData.tipo) return false;

                const tipoActual = movimientoData.tipo;

                // Descripción es obligatoria para todos
                if (!movimientoData.descripcion?.trim()) return false;

                // Cédula: requiere PDF
                if (esCedula(tipoActual)) {
                    return !!archivoPDF;
                }

                // Si el tipo requiere escrito, debe tener uno seleccionado
                if (tipoReqEscrito(tipoActual) && !movimientoData.escrito) {
                    return false;
                }

                return true;
            }
            if (accionSeleccionada === 'reporte') {
                // In template cards view, always allow (cards handle their own export)
                if (!modoPersonalizado) return true;
                // In personalizado mode, need fields selected
                return reporteConfig.campos.length > 0 && reporteConfig.titulo;
            }
        }

        return true;
    };

    // Toggle campo en la selección
    const toggleCampo = (campoId) => {
        setReporteConfig(prev => {
            const campos = [...prev.campos];
            const index = campos.indexOf(campoId);

            if (index > -1) {
                campos.splice(index, 1);
            } else {
                campos.push(campoId);
            }

            return { ...prev, campos };
        });

        // Cambiar a personalizado si se modifica manualmente
        if (plantillaPredefinida !== 'personalizado') {
            setPlantillaPredefinida('personalizado');
        }
    };

    // Exportar a Excel
    const exportarExcel = (configOverride) => {
        const config = configOverride || reporteConfig;
        // Guard: si la plantilla/config no trae campos definidos, evitar el crash
        // "Cannot read properties of undefined (reading 'forEach')".
        const campos = Array.isArray(config?.campos) ? config.campos : [];
        if (campos.length === 0) {
            setProgreso([{ id: 'excel', caratula: config?.titulo || 'Reporte', estado: 'error',
                mensaje: 'Seleccioná al menos un campo para exportar a Excel' }]);
            return;
        }
        try {
            const datos = expedientesProcesados.map(exp => {
                const row = {};
                campos.forEach(campoId => {
                    row[getLabelCampo(campoId, tipoExpediente)] = getValorCampo(exp, campoId);
                });
                return row;
            });

            const ws = XLSX.utils.json_to_sheet(datos);

            // Ajustar anchos de columna
            const colWidths = campos.map(campoId => {
                return { wch: Math.min(getAnchoColumna(campoId, tipoExpediente) / 6, 40) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Reporte');

            const fileName = `${config.titulo.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);

            setResumen(prev => ({ ...prev, exitosos: expedientesProcesados.length }));
            setProgreso([{
                id: 'excel',
                caratula: config.titulo,
                estado: 'exito',
                mensaje: `Excel generado con ${expedientesProcesados.length} registros`
            }]);
        } catch (error) {
            console.error('Error exportando Excel:', error);
        }
    };

    const dibujarGraficoEstados = (doc, estados, x, y, ancho, alto) => {
        const total = Object.values(estados).reduce((a, b) => a + b, 0);
        if (total === 0) return y;

        // Título
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(31, 75, 130);
        doc.text('Distribución por Estado', x, y);
        y += 20;

        const colores = [
            [66, 133, 244], [52, 168, 83], [251, 188, 5], [234, 67, 53],
            [156, 39, 176], [255, 112, 67], [0, 172, 193], [121, 134, 203]
        ];

        const entries = Object.entries(estados).sort((a, b) => b[1] - a[1]);
        const maxCantidad = Math.max(...entries.map(([_, cant]) => cant));
        const barHeight = 22;
        const barSpacing = 10;
        const maxBarWidth = ancho * 0.55; // 55% del ancho para la barra
        const estadoWidth = 150; // Ancho fijo para el nombre del estado
        const numerosX = x + estadoWidth + maxBarWidth + 15; // Posición fija para números

        entries.forEach(([estado, cantidad], index) => {
            const color = colores[index % colores.length];
            const porcentaje = ((cantidad / total) * 100).toFixed(1);
            const barWidth = (cantidad / maxCantidad) * maxBarWidth;

            // Nombre del estado - PRIMERO, alineado a la izquierda
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(60);
            // Normalizar texto: Primera letra mayúscula, resto minúscula
            const estadoNormalizado = estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
            doc.text(estadoNormalizado, x, y + 15);

            // Barra - empieza después del nombre del estado
            const barStartX = x + estadoWidth;
            doc.setFillColor(color[0], color[1], color[2]);
            doc.roundedRect(barStartX, y, barWidth, barHeight, 3, 3, 'F');

            // Cantidad y porcentaje - posición fija a la derecha
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(60);
            doc.text(`${cantidad}`, numerosX, y + 15);

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            doc.text(`(${porcentaje}%)`, numerosX + 25, y + 15);

            y += barHeight + barSpacing;
        });

        return y + 10;
    };

    const dibujarGraficoBarras = (doc, titulo, datos, x, y, ancho, maxItems = 10) => {
        // datos: { label: count, ... }
        const total = Object.values(datos).reduce((a, b) => a + b, 0);
        if (total === 0) return y;

        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(31, 75, 130);
        doc.text(titulo, x, y);
        y += 20;

        const colores = [
            [66, 133, 244], [52, 168, 83], [251, 188, 5], [234, 67, 53],
            [156, 39, 176], [255, 112, 67], [0, 172, 193], [121, 134, 203],
            [33, 150, 243], [76, 175, 80]
        ];

        const entries = Object.entries(datos).sort((a, b) => b[1] - a[1]).slice(0, maxItems);
        const maxCantidad = Math.max(...entries.map(([_, cant]) => cant));
        const barHeight = 18;
        const barSpacing = 6;
        const labelWidth = ancho * 0.3;
        const maxBarWidth = ancho * 0.5;
        const numerosX = x + labelWidth + maxBarWidth + 10;

        entries.forEach(([label, cantidad], index) => {
            // Page break si no cabe
            if (y + barHeight + barSpacing > pageHeight - 40) {
                doc.addPage();
                y = 40;
            }
            const color = colores[index % colores.length];
            const porcentaje = ((cantidad / total) * 100).toFixed(1);
            const barWidth = maxCantidad > 0 ? (cantidad / maxCantidad) * maxBarWidth : 0;

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(60);
            const truncLabel = label.length > 35 ? label.substring(0, 35) + '...' : label;
            doc.text(truncLabel, x, y + 12);

            doc.setFillColor(color[0], color[1], color[2]);
            doc.roundedRect(x + labelWidth, y, Math.max(barWidth, 2), barHeight, 2, 2, 'F');

            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(60);
            doc.text(`${cantidad} (${porcentaje}%)`, numerosX, y + 12);

            y += barHeight + barSpacing;
        });

        return y + 15;
    };

    const dibujarGraficoJuzgados = (doc, expedientes, x, y, ancho) => {
        const datos = {};
        expedientes.forEach(exp => {
            const j = exp.Juzgado || exp.juzgado || '';
            if (j) datos[j] = (datos[j] || 0) + 1;
        });
        return dibujarGraficoBarras(doc, 'Distribución por Juzgado', datos, x, y, ancho, 10);
    };

    const dibujarGraficoAbogados = (doc, expedientes, x, y, ancho) => {
        const abogados = {};
        expedientes.forEach(exp => {
            const a = exp.Abogados_Participantes || exp.Abogados || exp.abogados || '';
            if (a && a !== '-') {
                const nombres = a.split(/[,;]/).map(n => n.trim()).filter(Boolean);
                nombres.forEach(nombre => {
                    abogados[nombre] = (abogados[nombre] || 0) + 1;
                });
            } else {
                abogados['Sin abogado'] = (abogados['Sin abogado'] || 0) + 1;
            }
        });
        return dibujarGraficoBarras(doc, 'Distribución por Abogado (Top 10)', abogados, x, y, ancho, 10);
    };

    const dibujarGraficoMontosPorEstado = (doc, expedientes, x, y, ancho) => {
        const datos = {};
        expedientes.forEach(exp => {
            const estado = exp.Estado || exp.estado || 'Sin estado';
            const monto = Number(exp.Capital_Reclamado) || Number(exp.Monto_Capital) || Number(exp.Monto_Total) || 0;
            datos[`${estado}`] = (datos[`${estado}`] || 0) + monto;
        });
        // Format values for display
        const datosDisplay = {};
        Object.entries(datos).forEach(([k, v]) => { if (v > 0) datosDisplay[k] = v; });
        return dibujarGraficoBarras(doc, 'Montos por Estado (Capital)', datosDisplay, x, y, ancho, 10);
    };

    const dibujarGraficoEvolucionTemporal = (doc, expedientes, x, y, ancho) => {
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const porMes = {};
        expedientes.forEach(exp => {
            const fecha = parsearFecha(exp.Fecha_Inicio);
            if (fecha) {
                const key = `${meses[fecha.getMonth()]} ${fecha.getFullYear()}`;
                porMes[key] = (porMes[key] || 0) + 1;
            }
        });
        return dibujarGraficoBarras(doc, 'Evolución Temporal (por Fecha Inicio)', porMes, x, y, ancho, 24);
    };

    // Generar PDF con campos dinámicos
    const generarReportePDF = async (soloPreview = false, configOverride) => {
        const config = configOverride || reporteConfig;
        setEjecutando(true);

        try {
            const doc = new jsPDF({
                orientation: config.orientacion,
                unit: 'pt',
                format: 'a4'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const marginLeft = 30;
            const marginRight = 30;
            const usableWidth = pageWidth - marginLeft - marginRight;

            // Cargar logo
            const logoBase64 = await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.src = logoSIGIF;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = () => resolve(null);
            });

            // Header
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(23, 30, 60);
            doc.text('Fiscalía de Estado - San Juan', marginLeft, 40);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(31, 75, 130);
            doc.text(config.titulo, marginLeft, 58);

            // Descripcion opcional (DESPUES del titulo)
            let currentDescY = 70;
            if (config.descripcion) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(100);
                doc.text(config.descripcion, marginLeft, currentDescY);
                currentDescY += 12;
            }

            if (logoBase64) {
                doc.addImage(logoBase64, 'PNG', pageWidth - 140, 20, 100, 42);
            }

            doc.setFontSize(8);
            doc.setTextColor(100);
            let infoText = `Generado: ${new Date().toLocaleDateString('es-AR')} ${new Date().toLocaleTimeString('es-AR')} | Total: ${expedientesProcesados.length} expedientes`;
            if (filtroEstado) infoText += ` | Filtro: ${filtroEstado}`;
            if (ordenarPor) {
                const campoOrden = TODOS_LOS_CAMPOS.find(c => c.id === ordenarPor);
                infoText += ` | Ordenado por: ${campoOrden?.label || ordenarPor} (${ordenDireccion === 'asc' ? '↑' : '↓'})`;
            }
            doc.text(infoText, marginLeft, currentDescY + 5);  // ✅ Ajustar posición según si hay descripción

            let currentY = currentDescY + 15;  // ✅ Ajustar Y inicial

            if (config.soloGraficos) {
                // ===== MODO SOLO GRAFICOS =====
                const sel = config.graficosSeleccionados || ['estado', 'juzgado', 'abogado', 'montos', 'temporal'];

                const conteoEstados = {};
                expedientesProcesados.forEach(exp => {
                    const estado = exp.Estado || exp.estado || 'Sin estado';
                    conteoEstados[estado] = (conteoEstados[estado] || 0) + 1;
                });

                // 1) Distribución por Estado
                if (sel.includes('estado') && Object.keys(conteoEstados).length > 0) {
                    currentY = dibujarGraficoEstados(doc, conteoEstados, marginLeft, currentY, usableWidth, 180);
                }

                // 2) Distribución por Juzgado
                if (sel.includes('juzgado')) {
                    if (currentY > pageHeight - 100) { doc.addPage(); currentY = 40; }
                    currentY = dibujarGraficoJuzgados(doc, expedientesProcesados, marginLeft, currentY, usableWidth);
                }

                // 3) Distribución por Abogado (Top 10)
                if (sel.includes('abogado')) {
                    if (currentY > pageHeight - 100) { doc.addPage(); currentY = 40; }
                    currentY = dibujarGraficoAbogados(doc, expedientesProcesados, marginLeft, currentY, usableWidth);
                }

                // 4) Montos por Estado
                if (sel.includes('montos')) {
                    if (currentY > pageHeight - 100) { doc.addPage(); currentY = 40; }
                    currentY = dibujarGraficoMontosPorEstado(doc, expedientesProcesados, marginLeft, currentY, usableWidth);
                }

                // 5) Evolución temporal
                if (sel.includes('temporal')) {
                    if (currentY > pageHeight - 100) { doc.addPage(); currentY = 40; }
                    currentY = dibujarGraficoEvolucionTemporal(doc, expedientesProcesados, marginLeft, currentY, usableWidth);
                }

                // Footer en la última página
                const totalPages = doc.internal.getNumberOfPages();
                for (let p = 1; p <= totalPages; p++) {
                    doc.setPage(p);
                    doc.setFontSize(8);
                    doc.setTextColor(150);
                    doc.text(`Página ${p} de ${totalPages}`, pageWidth - marginRight - 50, pageHeight - 20);
                    doc.text('SIGIF - Sistema Integral de Gestión Informática Fiscalía de Estado', marginLeft, pageHeight - 20);
                }
            } else {
                // ===== MODO NORMAL CON TABLA =====

                // Preparar datos de la tabla
                const headers = config.campos.map(campoId => getLabelCampo(campoId, tipoExpediente));
                const columnWidths = config.campos.map(campoId => getAnchoColumna(campoId, tipoExpediente));

                // Calcular factor de escala para que las columnas quepan
                const totalWidth = columnWidths.reduce((a, b) => a + b, 0);
                const scaleFactor = totalWidth > usableWidth ? usableWidth / totalWidth : 1;
                const scaledWidths = columnWidths.map(w => w * scaleFactor);

                const tableData = expedientesProcesados.map(exp => {
                    return config.campos.map(campoId => getValorCampo(exp, campoId));
                });

                // Generar tabla con autoTable
                autoTable(doc, {
                    startY: currentY,
                    head: [headers],
                    body: tableData,
                    margin: { left: marginLeft, right: marginRight },
                    styles: {
                        fontSize: 7,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        lineWidth: 0.1
                    },
                    headStyles: {
                        fillColor: [31, 75, 130],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center'
                    },
                    columnStyles: config.campos.reduce((acc, campoId, idx) => {
                        acc[idx] = {
                            cellWidth: scaledWidths[idx],
                            halign: campoId.includes('Monto') || campoId.includes('Serie') || campoId.includes('Certificado') ? 'right' : 'left'
                        };
                        return acc;
                    }, {}),
                    didParseCell: (data) => {
                        // Colorear filas por estado si está habilitado
                        if (config.colorearEstados && data.section === 'body') {
                            const estadoIdx = config.campos.indexOf('Estado');
                            if (estadoIdx !== -1) {
                                const estadoRaw = data.row.raw[estadoIdx];
                                // Normalizar estado: quitar acentos, mayúsculas -> minúsculas, etc
                                const estadoNormalizado = estadoRaw
                                    .toLowerCase()
                                    .normalize("NFD")
                                    .replace(/[\u0300-\u036f]/g, ""); // quitar acentos

                                // Mapa de colores normalizado
                                const coloresNormalizados = {
                                    'en tramite': [232, 245, 233],          // Verde muy claro
                                    'cancelado': [227, 242, 253],           // Azul muy claro
                                    'archivado': [250, 250, 250],           // Gris muy claro
                                    'archivado moratoria': [255, 249, 196], // Amarillo claro
                                    'baja de certificado': [243, 229, 245], // Violeta claro
                                    'plan de pago dgr': [255, 235, 238],    // Naranja claro
                                    'sin estado': [224, 247, 250],          // Cian claro
                                    'concluido': [232, 234, 246],           // Índigo claro
                                    'default': [255, 255, 255]              // Blanco
                                };

                                const color = coloresNormalizados[estadoNormalizado] || coloresNormalizados['default'];
                                data.cell.styles.fillColor = color;
                            }
                        }
                    },
                    didDrawPage: (data) => {
                        // Footer en cada página
                        const pageNumber = doc.internal.getNumberOfPages();
                        doc.setFontSize(8);
                        doc.setTextColor(150);
                        doc.text(
                            `Página ${data.pageNumber} de ${pageNumber}`,
                            pageWidth - marginRight - 50,
                            pageHeight - 20
                        );
                        doc.text(
                            'SIGIF - Sistema Integral de Gestión Informática Fiscalía de Estado',
                            marginLeft,
                            pageHeight - 20
                        );
                    }
                });

                // Agregar resumen si está habilitado
                if (config.incluirResumen) {
                    const finalY = doc.lastAutoTable.finalY + 20;
                    if (finalY > pageHeight - 200) {
                        doc.addPage();
                        currentY = 40;
                    } else {
                        currentY = finalY;
                    }

                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(31, 75, 130);
                    doc.text('Resumen', marginLeft, currentY);
                    currentY += 15;

                    const conteoEstados = {};
                    expedientesProcesados.forEach(exp => {
                        const estado = exp.Estado || exp.estado || 'Sin estado';
                        conteoEstados[estado] = (conteoEstados[estado] || 0) + 1;
                    });

                    // ✅ NUEVO: Dibujar gráfico si está habilitado
                    if (config.incluirGrafico && Object.keys(conteoEstados).length > 0) {
                        currentY = dibujarGraficoEstados(doc, conteoEstados, marginLeft, currentY, usableWidth * 0.6, 180);
                        currentY += 10;
                    } else {
                        // Mostrar lista de texto si no hay gráfico
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(60);
                        Object.entries(conteoEstados).forEach(([estado, cantidad]) => {
                            doc.text(`• ${estado}: ${cantidad} expedientes`, marginLeft + 10, currentY);
                            currentY += 12;
                        });
                    }

                    // Total de montos si hay campos de monto
                    if (config.campos.includes('MontoTotal')) {
                        const totalMonto = expedientesProcesados.reduce((sum, exp) => {
                            return sum + (Number(exp.Capital_Reclamado) || 0);
                        }, 0);
                        currentY += 5;
                        doc.setFont('helvetica', 'bold');
                        doc.text(`Total General: $${totalMonto.toLocaleString('es-AR')}`, marginLeft + 10, currentY);
                    }
                }
            }

            if (soloPreview) {
                // Abrir vista previa
                const pdfBlob = doc.output('blob');
                const url = URL.createObjectURL(pdfBlob);
                setPreviewUrl(url);
                setPreviewOpen(true);
            } else {
                // Descargar
                const fileName = `${config.titulo.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                doc.save(fileName);

                setResumen({ exitosos: expedientesProcesados.length, errores: 0, total: expedientesProcesados.length });
                setProgreso([{
                    id: 'pdf',
                    caratula: config.titulo,
                    estado: 'exito',
                    mensaje: `PDF generado con ${expedientesProcesados.length} registros`
                }]);
            }

        } catch (error) {
            console.error('Error generando PDF:', error);
            setProgreso([{
                id: 'pdf-error',
                caratula: 'Error',
                estado: 'error',
                mensaje: error.message
            }]);
        } finally {
            setEjecutando(false);
        }
    };

    // Helper para leer un File como base64 (data URL)
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Ejecutar acción masiva
    const ejecutarAccionMasiva = async () => {
        if (accionSeleccionada === 'reporte') {
            await generarReportePDF(false);
            return;
        }

        setEjecutando(true);
        canceladoRef.current = false;

        const total = expedientesProcesados.length;
        let exitosos = 0;
        let errores = 0;
        const nuevoProgreso = [];

        // Si hay PDF (Cédula), lo leemos una sola vez en base64
        let adjuntoBase64Cache = null;
        if (archivoPDF && esCedula(movimientoData.tipo)) {
            try {
                adjuntoBase64Cache = await fileToBase64(archivoPDF);
            } catch (e) {
                console.error('Error leyendo archivo PDF:', e);
            }
        }

        for (let i = 0; i < expedientesProcesados.length; i++) {
            if (canceladoRef.current) {
                nuevoProgreso.push({
                    id: `cancel-${i}`,
                    caratula: 'Operación cancelada',
                    estado: 'cancelado',
                    mensaje: `Procesados ${i} de ${total}`
                });
                break;
            }

            const exp = expedientesProcesados[i];
            const expId = exp.Id_Expediente || exp.id_expediente || exp.Id || exp.id;
            const caratula = exp.Caratula || exp.caratula || exp.Expediente_Adm || `Expediente ${i + 1}`;

            try {
                if (accionSeleccionada === 'movimiento') {

                    // 1) Tipo "visual" que eligió el usuario en el combo
                    const tipoUI = (() => {
                        const t = movimientoData.tipo;
                        if (!t) return "";
                        if (typeof t === "string") return t;
                        return (
                            t.tipo ||
                            t.Tipo ||
                            t.label ||
                            t.labelUI ||
                            ""
                        );
                    })();

                    if (!tipoUI) {
                        throw new Error("No se pudo determinar el tipo de movimiento (campo 'tipo' vacío).");
                    }

                    // 2) Buscar el escrito seleccionado (si hay)
                    let escritoTexto = "";
                    let escritoObj = null;

                    if (movimientoData.escrito) {
                        const idSel = String(movimientoData.escrito);
                        escritoObj = (escritos || []).find(e =>
                            String(e.Id || e.id) === idSel
                        );

                        if (escritoObj) {
                            escritoTexto =
                                escritoObj.Escrito ||
                                escritoObj.escrito ||
                                escritoObj.Nombre ||
                                escritoObj.nombre ||
                                "";
                        } else {
                            escritoTexto = movimientoData.escrito;
                        }
                    }

                    // 3) Determinar el tipo a enviar a la API
                    // Si es "MIS ESCRITOS", enviamos exactamente eso para que la API
                    // resuelva el tipo real desde Escritos_Categorias
                    const esMisEscritos = isTipo(tipoUI, 'MIS ESCRITOS');
                    const tipoParaApi = esMisEscritos ? 'MIS ESCRITOS' : tipoUI;

                    // 4) Fecha con hora
                    const fechaConHora = /\d{2}:\d{2}/.test(movimientoData.fecha)
                        ? movimientoData.fecha
                        : `${movimientoData.fecha} ${new Date().toTimeString().slice(0, 8)}`;

                    // 5) Reemplazar variables en el escrito con datos del expediente
                    let contenidoFinal = htmlEscrito || "";
                    if (contenidoFinal) {
                        // Fecha actual
                        const hoy = new Date();
                        const dd = String(hoy.getDate()).padStart(2, '0');
                        const mm = String(hoy.getMonth() + 1).padStart(2, '0');
                        const yyyy = hoy.getFullYear();
                        const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

                        const v = (field) => exp[field] ?? '';
                        const vars = {
                            // Fechas del sistema
                            Fecha: `${dd}/${mm}/${yyyy}`,
                            Fecha_ISO: `${yyyy}-${mm}-${dd}`,
                            Fecha_Letras: `${dd} de ${meses[hoy.getMonth()]} de ${yyyy}`,
                            Fecha_Mes_Anio: `${dd} de ${meses[hoy.getMonth()].toUpperCase()} ${yyyy}`,
                            Dia: dd,
                            Mes: meses[hoy.getMonth()].toUpperCase(),
                            Anio: String(yyyy),
                            Fecha_Movimiento: fechaConHora.split(' ')[0] || '',
                            // Identificación
                            Expediente: String(expId),
                            Caratula: v('Caratula'),
                            Autos: v('Autos_1_Instancia') || v('Autos'),
                            Autos_1_Instancia: v('Autos_1_Instancia') || v('Autos'),
                            Autos_2_Instancia: v('Autos_2_Instancia'),
                            Autos_Extra: v('Autos_Extra'),
                            Autos_Extra_Fed: v('Autos_Extra_Fed'),
                            Tipo_Proceso: v('Tipo_Proceso'),
                            Objeto_Reclamo: v('Objeto_Reclamo'),
                            Estado: v('Estado'),
                            Estado_Proceso: v('Estado'),
                            Tramite: v('Tramite'),
                            Modulo: v('Modulo'),
                            // Radicación / Juzgado
                            Juzgado: v('Juzgado'),
                            Radicacion: v('Juzgado'),
                            Capital_Reclamado: (() => {
                                const m = parseFloat(v('Capital_Reclamado')) || parseFloat(v('Monto_Actualizado_Demanda')) || 0;
                                return m > 0 ? m.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '';
                            })(),
                            Capital_Reclamado_Letras: (() => {
                                const m = parseFloat(v('Capital_Reclamado')) || parseFloat(v('Monto_Actualizado_Demanda')) || 0;
                                return m > 0 ? numeroALetras(m) : '';
                            })(),
                            Monto_Total: (() => {
                                const m = parseFloat(v('Capital_Reclamado')) || parseFloat(v('Monto_Actualizado_Demanda')) || 0;
                                return m > 0 ? m.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '';
                            })(),
                            Monto_Actualizado: (() => {
                                const m = parseFloat(v('Monto_Actualizado_Demanda')) || 0;
                                return m > 0 ? m.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '';
                            })(),
                            Domicilio_Constituido: v('Domicilio_Constituido'),
                            // Partes
                            Demandado: v('Demandado'),
                            Demandado_NN: v('Demandado'),
                            Demandado_Direccion: v('Demandado_Direccion'),
                            Demandado_DNI: v('Demandado_DNI'),
                            Demandado_Localidad: v('Demandado_Localidad'),
                            Actor: v('Actor'),
                            Abogados: v('Abogados_Participantes'),
                            Abogados_Participantes: v('Abogados_Participantes'),
                            Abogado: v('Abogados_Participantes'),
                            Abogado_Detalle: v('Abogado_Detalle'),
                            // Administrativos
                            Expediente_Adm: v('Expediente_Adm'),
                            Nro_Expediente: v('Expediente_Adm'),
                            Iniciador: v('Iniciador'),
                            Extracto: v('Extracto'),
                            Reparticion_Origen: v('Reparticion_Origen'),
                            // Fechas expediente
                            Fecha_Inicio: v('Fecha_Inicio'),
                            Fecha_Asignacion: v('Fecha_Asignacion'),
                            Fecha_Sentencia_1_Ins: v('Fecha_Sentencia_1_Ins'),
                            Fecha_Sentencia_2_Ins: v('Fecha_Sentencia_2_Ins'),
                            // Resultados y ubicación
                            Resultado_1: v('Resultado1'),
                            Resultado_2: v('Resultado2'),
                            Cantidad_Cuerpos: v('Cantidad_Cuerpos'),
                            Cantidad_Fojas: v('Cantidad_Fojas'),
                        };
                        // Reemplazo case-insensitive de {Variable}
                        const upperVars = {};
                        Object.entries(vars).forEach(([k, v]) => { upperVars[k.toUpperCase()] = String(v || ''); });
                        contenidoFinal = contenidoFinal.replace(/\{(\w+)\}/g, (match, key) => {
                            const val = upperVars[key.toUpperCase()];
                            return (val !== undefined && val !== '') ? val : match;
                        });
                    }

                    // 6) Armamos payload
                    const payload = {
                        fecha: fechaConHora,
                        tipo: tipoParaApi,
                        descripcion: movimientoData.descripcion || "",
                        usuario: usuarioLogin || usuario || "",
                        idExpediente: expId,
                        escrito: escritoTexto || "",
                        escritoContenido: contenidoFinal
                    };

                    // 6) Adjuntos PDF (Cédula)
                    if (adjuntoBase64Cache && archivoPDF) {
                        payload.adjuntoNombre = archivoPDF.name;
                        const base64Solo = String(adjuntoBase64Cache).includes(",")
                            ? String(adjuntoBase64Cache).split(",")[1]
                            : adjuntoBase64Cache;
                        payload.adjuntoBase64 = base64Solo;
                    }

                    const movData = {
                        fecha: payload.fecha,
                        tipo: payload.tipo,
                        descripcion: payload.descripcion || '',
                        escrito: payload.escrito || '',
                        escritoContenido: payload.escritoContenido || '',
                        responsable: payload.usuario || '',
                    };

                    // Adjuntar PDF (Cédula)
                    if (payload.adjuntoBase64 && payload.adjuntoNombre) {
                        movData.adjuntoBase64 = payload.adjuntoBase64;
                        movData.adjuntoNombre = payload.adjuntoNombre;
                    }

                    const response = await crearMovimientoJWT(expId, movData);

                    if (response?.ok) {
                        exitosos++;
                        nuevoProgreso.push({
                            id: expId,
                            caratula,
                            estado: "exito",
                            mensaje: "Movimiento insertado correctamente"
                        });
                    } else {
                        throw new Error(response?.mensaje || response?.error || "Error al crear movimiento.");
                    }
                }


            } catch (error) {
                errores++;
                nuevoProgreso.push({
                    id: expId,
                    caratula,
                    estado: 'error',
                    mensaje: error.message || 'Error al procesar'
                });
                console.error(`Error procesando "${caratula}":`, error);
            }

            setProgreso([...nuevoProgreso]);
            setResumen({ exitosos, errores, total });

            // Pequeña pausa para no saturar el servidor
            if (i < expedientesProcesados.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        // ✅ al terminar, fijamos el resumen final
        //setResumen({ exitosos, errores, total });
        setEjecutando(false);

        if (onSuccess && exitosos > 0) {
            onSuccess({ exitosos, errores, total });
        }
    };

    // Cancelar ejecución
    const cancelar = () => {
        canceladoRef.current = true;
    };

    // Aplicar plantilla guardada
    const aplicarPlantillaGuardada = (plantilla) => {
        if (!plantilla) return;

        // Parsear configuracion
        let cfg = {};
        if (plantilla.configParsed && typeof plantilla.configParsed === 'object') {
            cfg = plantilla.configParsed;
        } else {
            try { cfg = JSON.parse(plantilla.Configuracion || '{}'); } catch { cfg = {}; }
        }

        const newConfig = {
            ...reporteConfig,
            campos: cfg.campos || reporteConfig.campos || [],
            titulo: cfg.titulo || plantilla.Nombre || reporteConfig.titulo,
            orientacion: cfg.orientacion || reporteConfig.orientacion,
            incluirResumen: typeof cfg.incluirResumen === 'boolean' ? cfg.incluirResumen : reporteConfig.incluirResumen,
            incluirGrafico: typeof cfg.incluirGrafico === 'boolean' ? cfg.incluirGrafico : reporteConfig.incluirGrafico,
            colorEstados: typeof cfg.colorearEstados === 'boolean' ? cfg.colorearEstados : reporteConfig.colorEstados,
        };

        setReporteConfig(newConfig);
        // No cerramos plantillas - el preview se muestra encima
        generarReportePDF(true, newConfig);
    };


    // Renderizar paso actual
    const renderPaso = () => {
        if (pasoActual === 0) {
            // Paso 1: Seleccionar acción
            return (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Selecciona la acción a realizar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Se aplicará a {expedientesProcesados.length} expedientes
                        {filtroEstado && ` (filtrado por: ${filtroEstado})`}
                    </Typography>

                    <Grid container spacing={3} style={{ marginTop: 16 }}>
                        {ACCIONES.map(accion => {
                            const Icon = accion.icon;
                            const selected = accionSeleccionada === accion.id;

                            return (
                                <Grid size={{ xs: 12, md: 4 }} key={accion.id}>
                                    <Paper
                                        className={`${classes.accionCard} ${selected ? classes.accionCardSelected : ''}`}
                                        onClick={() => setAccionSeleccionada(accion.id)}
                                        elevation={selected ? 8 : 2}
                                    >
                                        <Box textAlign="center">
                                            <Icon
                                                className={classes.accionIcon}
                                                style={{ color: accion.color }}
                                            />
                                            <Typography variant="h6" gutterBottom>
                                                {accion.titulo}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {accion.descripcion}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            );
        }

        if (pasoActual === 1) {
            // Paso 2: Configurar acción
            if (accionSeleccionada === 'movimiento') {

                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Configurar Movimiento
                        </Typography>
                        {/* Info de expedientes afectados */}
                        <Paper style={{
                            padding: '12px 16px',
                            backgroundColor: '#e3f2fd',
                            marginBottom: 20,
                            borderLeft: '4px solid #1976d2'
                        }}>
                            <Typography variant="body2">
                                <strong>{expedientesProcesados.length}</strong> expediente(s) serán afectados
                                {filtroEstado && <span> (filtrado por: <strong>{filtroEstado}</strong>)</span>}
                            </Typography>
                        </Paper>
                        <Grid container spacing={3}>
                            {/* Fecha */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Fecha del movimiento"
                                    type="date"
                                    value={movimientoData.fecha}
                                    onChange={(e) => setMovimientoData(prev => ({ ...prev, fecha: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                />
                            </Grid>

                            {/* Tipo de movimiento */}
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Autocomplete
                                    options={tiposParaMenu}
                                    getOptionLabel={(option) => {
                                        if (option.isDivider) return '';
                                        return displayTipoLabel(getTipoNombre(option));
                                    }}
                                    getOptionDisabled={(option) => option.isDivider === true}
                                    renderOption={(props, option) => {
                                        if (option.isDivider) {
                                            return (
                                                <li {...props} key={option.label} style={{ padding: 0 }}>
                                                    <Divider
                                                        style={{ backgroundColor: '#5d7b9d', margin: '2px 0', width: '100%' }}
                                                    />
                                                </li>
                                            );
                                        }

                                        const nombre = getTipoNombre(option);
                                        const k = keyify(nombre);

                                        // Determinar ícono según tipo
                                        let Icon = DescriptionIcon;
                                        if (k === keyify('CEDULA')) Icon = AssignmentIcon;
                                        else if (k === keyify('REFERENCIA')) Icon = BookmarkIcon;
                                        else if (k === keyify('PROCESAL')) Icon = GavelIcon;
                                        else if (k === keyify('EXTRA PROCESAL')) Icon = AttachFileIcon;
                                        else if (k === keyify('MIS ESCRITOS')) Icon = PersonIcon;
                                        else if (k === keyify('OTRO MOV')) Icon = FolderIcon;
                                        else if (k === keyify('RECIBOS')) Icon = ReceiptIcon;
                                        else if (k === keyify('OFIGEN') || k === keyify('OFICINA DE PAGOS')) Icon = ReceiptIcon;

                                        return (
                                            <li {...props} key={option.Id || option.id || nombre}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                                                    <Icon style={{ fontSize: 20, color: '#1976d2', opacity: 0.8 }} />
                                                    <span>{displayTipoLabel(nombre)}</span>
                                                </div>
                                            </li>
                                        );
                                    }}
                                    value={(() => {
                                        if (!movimientoData.tipo) return null;

                                        const found = tiposParaMenu.find(
                                            t => !t.isDivider && keyify(getTipoNombre(t)) === keyify(movimientoData.tipo)
                                        );
                                        return found || null;
                                    })()}
                                    onChange={(_, newValue) => {
                                        if (!newValue || newValue.isDivider) return;

                                        const nuevoTipo = getTipoNombre(newValue);

                                        setMovimientoData(prev => ({
                                            ...prev,
                                            tipo: nuevoTipo,
                                            escrito: '',
                                            descripcion: ''
                                        }));
                                        setHtmlEscrito('');
                                        setArchivoPDF(null);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Tipo de movimiento *"
                                            variant="outlined"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Selector de escrito (solo si el tipo lo requiere) */}
                            {tipoReqEscrito(movimientoData.tipo) && (
                                <Grid size={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Escrito *</InputLabel>
                                        <Select
                                            value={movimientoData.escrito}
                                            onChange={(e) => setMovimientoData(prev => ({
                                                ...prev,
                                                escrito: e.target.value
                                            }))}
                                            label="Escrito *"
                                            disabled={loadingEscritos}
                                        >
                                            {escritosFiltrados.length === 0 ? (
                                                <MenuItem disabled>
                                                    No hay escritos disponibles para este tipo
                                                </MenuItem>
                                            ) : (
                                                escritosFiltrados.map((esc) => {
                                                    const id = esc.Id || esc.id;
                                                    const nombre = esc.Nombre || esc.nombre || esc.escrito || '';
                                                    return (
                                                        <MenuItem key={id} value={id}>
                                                            {nombre}
                                                        </MenuItem>
                                                    );
                                                })
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Descripción */}
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label={
                                        isTipo(movimientoData.tipo, 'REFERENCIA', 'PROCESAL', 'EXTRA PROCESAL')
                                            ? "Descripción *"
                                            : "Descripción (opcional)"
                                    }
                                    value={movimientoData.descripcion}
                                    onChange={(e) => setMovimientoData(prev => ({ ...prev, descripcion: e.target.value }))}
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    placeholder={
                                        isTipo(movimientoData.tipo, 'REFERENCIA')
                                            ? "Ingrese el contenido de la referencia..."
                                            : "Descripción adicional del movimiento..."
                                    }
                                    required={isTipo(movimientoData.tipo, 'REFERENCIA', 'PROCESAL', 'EXTRA PROCESAL')}
                                    error={
                                        isTipo(movimientoData.tipo, 'REFERENCIA', 'PROCESAL', 'EXTRA PROCESAL') &&
                                        !movimientoData.descripcion?.trim()
                                    }
                                    helperText={
                                        isTipo(movimientoData.tipo, 'REFERENCIA')
                                            ? "Obligatorio: Este texto será el contenido de la referencia"
                                            : isTipo(movimientoData.tipo, 'PROCESAL', 'EXTRA PROCESAL')
                                                ? "Obligatorio: Descripción del movimiento"
                                                : ""
                                    }
                                />
                            </Grid>

                            {/* Adjuntar PDF para Cédula */}
                            {esCedula(movimientoData.tipo) && (
                                <Grid size={12}>
                                    <Paper style={{
                                        padding: 16,
                                        backgroundColor: '#f5f5f5',
                                        border: '2px dashed #ccc',
                                        borderRadius: 4
                                    }}>
                                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                                            Adjuntar Cédula (PDF)
                                        </Typography>

                                        <input
                                            accept="application/pdf"
                                            style={{ display: 'none' }}
                                            id="archivo-pdf-input"
                                            type="file"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    setArchivoPDF(file);
                                                    // Actualizar descripción solo con el nombre del archivo (sin extensión)
                                                    const nombreSinExt = file.name.replace(/\.[^/.]+$/, "");
                                                    setMovimientoData(prev => ({
                                                        ...prev,
                                                        descripcion: nombreSinExt
                                                    }));
                                                }
                                            }}
                                        />
                                        <label htmlFor="archivo-pdf-input">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<AssignmentIcon />}
                                                style={{ marginRight: 12 }}
                                            >
                                                Seleccionar archivo
                                            </Button>
                                        </label>

                                        {archivoPDF && (
                                            <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
                                                <Box display="flex" alignItems="center">
                                                    <DescriptionIcon style={{ marginRight: 8, color: '#d32f2f' }} />
                                                    <Typography variant="body2">
                                                        {archivoPDF.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" style={{ marginLeft: 8 }}>
                                                        ({(archivoPDF.size / 1024).toFixed(1)} KB)
                                                    </Typography>
                                                </Box>
                                                <IconButton size="small" onClick={() => setArchivoPDF(null)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}

                                        {!archivoPDF && (
                                            <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: 8 }}>
                                                Seleccione un archivo PDF para adjuntar al movimiento
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            )}

                            {/* Editor TinyMCE (si hay escrito seleccionado) */}
                            {movimientoData.escrito && (
                                <Grid size={12}>
                                    <Paper style={{ padding: 16 }}>
                                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                                            Contenido del escrito
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Este contenido se aplicará a cada expediente con sus variables reemplazadas
                                        </Typography>

                                        {cargandoEscrito ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                                <CircularProgress size={32} />
                                                <Typography variant="body2" color="text.secondary" style={{ marginLeft: 12 }}>
                                                    Cargando escrito...
                                                </Typography>
                                            </Box>
                                        ) : !htmlEscrito ? (
                                            <Box py={4} textAlign="center">
                                                <Alert severity="info">
                                                    Este escrito no tiene contenido de plantilla. Se insertará el movimiento sin escrito adjunto.
                                                </Alert>
                                            </Box>
                                        ) : (
                                            <TinyEditorWrapper
                                                key={movimientoData.escrito}
                                                htmlEscrito={htmlEscrito}
                                                setHtmlEscrito={setHtmlEscrito}
                                            />
                                        )}
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                        {/* Resumen de expedientes a procesar */}
                        {filtroEstado && (
                            <Box mt={2}>
                                <Alert severity="info" icon={<FilterIcon />}>
                                    Se procesarán <strong>{expedientesProcesados.length}</strong> de {expedientes.length} expedientes
                                    (filtrado por estado: <strong>{filtroEstado}</strong>)
                                </Alert>
                            </Box>
                        )}
                    </Box>
                );
            }

            if (accionSeleccionada === 'reporte') {
                // Show "Personalizado" panel if user clicked it
                if (modoPersonalizado) {
                    return (
                        <Box>
                            {/* Row 1: Title + Description */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                <TextField
                                    label="Título del reporte"
                                    value={reporteConfig.titulo}
                                    onChange={(e) => setReporteConfig(prev => ({ ...prev, titulo: e.target.value }))}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Descripción (opcional)"
                                    value={reporteConfig.descripcion || ''}
                                    onChange={(e) => setReporteConfig(prev => ({ ...prev, descripcion: e.target.value }))}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            </Box>

                            {/* Row 2: Options */}
                            <Paper variant="outlined" sx={{ display: 'flex', gap: 2, mb: 1.5, p: 1, alignItems: 'center', flexWrap: 'wrap', borderRadius: '4px' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mr: 1 }}>Opciones:</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Orientación:</Typography>
                                    <Button size="small" variant={reporteConfig.orientacion === 'portrait' ? 'contained' : 'outlined'}
                                        onClick={() => setReporteConfig(prev => ({ ...prev, orientacion: 'portrait' }))}
                                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, py: 0.25, borderRadius: '6px' }}>
                                        Vertical
                                    </Button>
                                    <Button size="small" variant={reporteConfig.orientacion === 'landscape' ? 'contained' : 'outlined'}
                                        onClick={() => setReporteConfig(prev => ({ ...prev, orientacion: 'landscape' }))}
                                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, py: 0.25, borderRadius: '6px' }}>
                                        Horizontal
                                    </Button>
                                </Box>
                                <FormControlLabel
                                    control={<Checkbox checked={reporteConfig.incluirResumen} onChange={(e) => setReporteConfig(prev => ({ ...prev, incluirResumen: e.target.checked }))} size="small" />}
                                    label={<Typography variant="caption">Resumen</Typography>}
                                    sx={{ mr: 0 }}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={reporteConfig.incluirGrafico} onChange={(e) => setReporteConfig(prev => ({ ...prev, incluirGrafico: e.target.checked }))} size="small" />}
                                    label={<Typography variant="caption">Gráfico</Typography>}
                                    sx={{ mr: 0 }}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={reporteConfig.colorEstados} onChange={(e) => setReporteConfig(prev => ({ ...prev, colorEstados: e.target.checked }))} size="small" />}
                                    label={<Typography variant="caption">Color estados</Typography>}
                                    sx={{ mr: 0 }}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={reporteConfig.soloGraficos} onChange={(e) => setReporteConfig(prev => ({ ...prev, soloGraficos: e.target.checked }))} size="small" />}
                                    label={<Typography variant="caption">Solo gráficos</Typography>}
                                    sx={{ mr: 0 }}
                                />
                            </Paper>

                            {/* If soloGraficos, show chart options instead of field selector */}
                            {reporteConfig.soloGraficos ? (
                                <Paper sx={{ p: 2, borderRadius: '4px' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Gráficos a incluir en el reporte:</Typography>
                                    <Grid container spacing={1}>
                                        {[
                                            { key: 'estado', label: 'Distribución por Estado', desc: 'Cantidad de expedientes por estado procesal' },
                                            { key: 'juzgado', label: 'Distribución por Juzgado', desc: 'Top juzgados con más causas' },
                                            { key: 'abogado', label: 'Distribución por Abogado', desc: 'Top 10 abogados por cantidad de causas' },
                                            { key: 'montos', label: 'Montos por Estado', desc: 'Capital total agrupado por estado' },
                                            { key: 'temporal', label: 'Evolución Temporal', desc: 'Cantidad de causas iniciadas por mes' },
                                        ].map(g => {
                                            const checked = reporteConfig.graficosSeleccionados?.includes(g.key) !== false;
                                            return (
                                                <Grid size={{ xs: 12, sm: 6 }} key={g.key}>
                                                    <Paper variant="outlined" sx={{
                                                        p: 1.5, borderRadius: '4px', cursor: 'pointer',
                                                        borderColor: checked ? 'primary.main' : '#e0e0e0',
                                                        bgcolor: checked ? 'primary.50' : 'transparent',
                                                        '&:hover': { borderColor: 'primary.main' },
                                                    }}
                                                        onClick={() => {
                                                            setReporteConfig(prev => {
                                                                const current = prev.graficosSeleccionados || ['estado', 'juzgado', 'abogado', 'montos', 'temporal'];
                                                                const next = current.includes(g.key)
                                                                    ? current.filter(k => k !== g.key)
                                                                    : [...current, g.key];
                                                                return { ...prev, graficosSeleccionados: next };
                                                            });
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.label}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{g.desc}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Paper>
                            ) : (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Paper sx={{ p: 1.5, maxHeight: 350, overflow: 'auto' }}>
                                        {Object.entries(getCamposDisponibles(tipoExpediente)).map(([grupo, campos]) => (
                                            <Box key={grupo} sx={{ mb: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1769aa', textTransform: 'uppercase', fontSize: 10, display: 'block', mb: 0.5 }}>
                                                    {grupo}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {campos.map(campo => {
                                                        const selected = reporteConfig.campos.includes(campo.id);
                                                        return (
                                                            <Chip
                                                                key={campo.id}
                                                                label={campo.label}
                                                                size="small"
                                                                onClick={() => toggleCampo(campo.id)}
                                                                color={selected ? 'primary' : 'default'}
                                                                variant={selected ? 'filled' : 'outlined'}
                                                                sx={{ cursor: 'pointer', fontSize: 11 }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Paper sx={{ p: 1.5, maxHeight: 350, overflow: 'auto' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            Vista previa ({reporteConfig.campos.length} campos · primeros 5 registros)
                                        </Typography>
                                        {reporteConfig.campos.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Selecciona campos a la izquierda
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <TableContainer>
                                                <Table size="small" sx={{ '& th, & td': { fontSize: 11, py: 0.5, px: 1, whiteSpace: 'nowrap' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            {reporteConfig.campos.map(campoId => (
                                                                <TableCell key={campoId} sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>
                                                                    {getLabelCampo(campoId, tipoExpediente)}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {expedientesProcesados.slice(0, 5).map((exp, idx) => (
                                                            <TableRow key={idx}>
                                                                {reporteConfig.campos.map(campoId => (
                                                                    <TableCell key={campoId}>
                                                                        {String(getValorCampo(exp, campoId)).substring(0, 30)}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                            )}
                        </Box>
                    );
                }

                // Main view: Template cards grid
                return (
                    <Box>
                        {/* Header with tabs */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Tabs
                                value={reporteTabView}
                                onChange={(e, v) => setReporteTabView(v)}
                                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 } }}
                            >
                                <Tab label="Plantillas" />
                                <Tab label="Mis reportes" />
                            </Tabs>
                            <Chip size="small" label={`${expedientesProcesados.length} expedientes`} color="primary" variant="outlined" />
                        </Box>

                        {/* Optional filter bar - collapsible */}
                        <Accordion disableGutters sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '4px !important' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                <Typography variant="body2" color="text.secondary">
                                    <FilterIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Filtrar y ordenar (opcional)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                                        <InputLabel>Estado</InputLabel>
                                        <Select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} label="Estado">
                                            <MenuItem value=""><em>Todos</em></MenuItem>
                                            {estadosUnicos.map(estado => (
                                                <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                                        <InputLabel>Ordenar por</InputLabel>
                                        <Select value={ordenarPor} onChange={(e) => setOrdenarPor(e.target.value)} label="Ordenar por">
                                            <MenuItem value=""><em>Sin orden</em></MenuItem>
                                            {getTodosCampos(tipoExpediente).map(campo => (
                                                <MenuItem key={campo.id} value={campo.id}>{campo.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {ordenarPor && (
                                        <Button size="small" onClick={() => setOrdenDireccion(prev => prev === 'asc' ? 'desc' : 'asc')}
                                            startIcon={ordenDireccion === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}>
                                            {ordenDireccion === 'asc' ? 'Ascendente' : 'Descendente'}
                                        </Button>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Template cards grid */}
                        <Grid container spacing={1.5}>
                            {(reporteTabView === 0 ? plantillasGlobales : plantillasUsuario).map(pl => {
                                const isGlobal = (pl.Scope || pl.scope || '').toUpperCase() === 'GLOBAL';
                                const nombre = pl.Nombre || pl.nombre;
                                const desc = pl.Descripcion || pl.descripcion || '';
                                let cfg = {};
                                try { cfg = typeof pl.Configuracion === 'string' ? JSON.parse(pl.Configuracion) : (pl.Configuracion || {}); } catch {}
                                // Las plantillas viejas (sobre todo las de Administrativos: "Listado
                                // Básico", "Reporte Completo", "Resumen Ejecutivo") guardan flags
                                // incluirEstado/incluirFechas/incluirResponsable en vez de un array
                                // `campos`. Sin `campos` el export quedaba vacío y "no hacía nada".
                                // Derivamos un set base para que funcionen igual.
                                let campos = Array.isArray(cfg.campos) && cfg.campos.length > 0 ? cfg.campos : [];
                                if (campos.length === 0) {
                                    const base = tipoExpediente === 2
                                        ? ['NumeroExpediente', 'Extracto', 'Iniciador']
                                        : ['Caratula', 'Autos'];
                                    campos = [...base];
                                    if (cfg.incluirEstado) campos.push('Estado');
                                    if (cfg.incluirFechas) campos.push('FechaInicio');
                                    if (cfg.incluirResponsable && tipoExpediente !== 2) campos.push('Abogado');
                                }
                                const orientacion = cfg.orientacion || 'landscape';
                                const incluirResumen = cfg.incluirResumen !== false;
                                const incluirGrafico = cfg.incluirGrafico !== false;
                                const color = isGlobal ? 'primary.main' : '#ff9800';
                                const hoverColor = isGlobal ? 'primary.main' : '#ff9800';

                                return (
                                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={pl.Id || pl.id}>
                                        <Card variant="outlined" sx={{
                                            borderRadius: '4px', transition: 'all 0.2s',
                                            '&:hover': { boxShadow: 4, borderColor: hoverColor },
                                            ...(isGlobal ? {} : { borderColor: '#ffe0b2' }),
                                        }}>
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        {isGlobal ? <DescriptionIcon sx={{ fontSize: 16 }} /> : <StarIcon sx={{ fontSize: 16 }} />}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>{nombre}</Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, mb: 1 }} noWrap>
                                                    {desc || `${campos.length} campos`}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Button size="small" variant="outlined"
                                                        onClick={() => {
                                                            const cardConfig = { ...reporteConfig, campos, titulo: nombre, descripcion: desc, orientacion, incluirResumen, incluirGrafico };
                                                            setReporteConfig(cardConfig);
                                                            exportarExcel(cardConfig);
                                                        }}
                                                        sx={{ textTransform: 'none', fontSize: 11, borderRadius: '6px', flex: 1, color: '#2e7d32', borderColor: '#2e7d32', py: 0.25, minWidth: 0 }}>
                                                        Excel
                                                    </Button>
                                                    <Button size="small" variant="outlined"
                                                        onClick={() => {
                                                            const cardConfig = { ...reporteConfig, campos, titulo: nombre, descripcion: desc, orientacion, incluirResumen, incluirGrafico };
                                                            setReporteConfig(cardConfig);
                                                            generarReportePDF(true, cardConfig);
                                                        }}
                                                        sx={{ textTransform: 'none', fontSize: 11, borderRadius: '6px', flex: 1, py: 0.25, minWidth: 0, color: '#d32f2f', borderColor: '#d32f2f' }}>
                                                        PDF
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}

                            {/* Personalizado card */}
                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: '4px',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': { boxShadow: 4, borderColor: '#9c27b0' },
                                        borderStyle: 'dashed',
                                        borderColor: '#ce93d8',
                                    }}
                                    onClick={() => {
                                        setReporteConfig(prev => ({ ...prev, campos: [], titulo: 'Reporte Personalizado' }));
                                        setModoPersonalizado(true);
                                    }}
                                >
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: '4px',
                                            bgcolor: '#f3e5f5', color: '#9c27b0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mx: 'auto', mb: 0.5,
                                        }}>
                                            <SettingsIcon sx={{ fontSize: 16 }} />
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Personalizado
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                            Elegí los campos
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                );
            }
        }

        if (pasoActual === 2) {
            // Paso 3: Ejecutar
            return (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {ejecutando ? 'Ejecutando...' : 'Resultado'}
                    </Typography>

                    {ejecutando && (
                        <Box mb={3}>
                            <LinearProgress
                                variant="determinate"
                                value={(progreso.length / expedientesProcesados.length) * 100}
                            />
                            <Typography variant="body2" color="text.secondary" align="center" style={{ marginTop: 8 }}>
                                Procesando {progreso.length} de {expedientesProcesados.length}
                            </Typography>
                        </Box>
                    )}

                    {/* Resumen */}
                    <Grid container spacing={2} style={{ marginBottom: 24 }}>
                        <Grid size={4}>
                            <Paper className={classes.statsCard} style={{ backgroundColor: '#e8f5e9' }}>
                                <CheckCircleIcon style={{ color: '#4caf50', fontSize: 32 }} />
                                <Typography variant="h4">{resumen.exitosos}</Typography>
                                <Typography variant="body2">Exitosos</Typography>
                            </Paper>
                        </Grid>
                        <Grid size={4}>
                            <Paper className={classes.statsCard} style={{ backgroundColor: '#ffebee' }}>
                                <ErrorIcon style={{ color: '#f44336', fontSize: 32 }} />
                                <Typography variant="h4">{resumen.errores}</Typography>
                                <Typography variant="body2">Errores</Typography>
                            </Paper>
                        </Grid>
                        <Grid size={4}>
                            <Paper className={classes.statsCard} style={{ backgroundColor: '#e3f2fd' }}>
                                <AssignmentIcon style={{ color: '#1976d2', fontSize: 32 }} />
                                <Typography variant="h4">{resumen.total || expedientesProcesados.length}</Typography>
                                <Typography variant="body2">Total</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Lista de progreso */}
                    <Box style={{ maxHeight: 300, overflow: 'auto' }}>
                        {progreso.map((item, index) => (
                            <Box
                                key={item.id || index}
                                className={`${classes.progressItem} ${item.estado === 'exito' ? classes.progressSuccess :
                                        item.estado === 'error' ? classes.progressError :
                                            classes.progressPending
                                    }`}
                            >
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center">
                                        {item.estado === 'exito' && <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} />}
                                        {item.estado === 'error' && <ErrorIcon style={{ color: '#f44336', marginRight: 8 }} />}
                                        {item.estado === 'cancelado' && <WarningIcon style={{ color: '#ff9800', marginRight: 8 }} />}
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                {item.caratula}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.mensaje}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            );
        }

        return null;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle className={classes.header}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">
                                Acciones Masivas
                            </Typography>
                            <Typography variant="body2" style={{ opacity: 0.9 }}>
                                {expedientes.length} expedientes seleccionados
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} style={{ color: 'white' }} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.content}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Stepper */}
                            <Stepper activeStep={pasoActual} style={{ marginTop: 8, marginBottom: 24, backgroundColor: 'transparent' }}>
                                {PASOS.map((label, index) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {/* Contenido del paso */}
                            {renderPaso()}
                        </>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: 16, backgroundColor: '#f5f5f5' }}>
                    {pasoActual === 0 && (
                        <Button onClick={onClose}>
                            Cancelar
                        </Button>
                    )}

                    {pasoActual > 0 && pasoActual < 2 && (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => {
                                if (modoPersonalizado) {
                                    setModoPersonalizado(false);
                                } else {
                                    setPasoActual(prev => prev - 1);
                                }
                            }}
                        >
                            Anterior
                        </Button>
                    )}

                    {pasoActual === 1 && accionSeleccionada === 'reporte' && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SettingsIcon />}
                                onClick={() => setPlantillasManagerOpen(true)}
                                sx={{ borderRadius: '4px', textTransform: 'none' }}
                            >
                                Plantillas
                            </Button>
                            {modoPersonalizado && (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={() => { setPlantillasAbrirEditor(true); setPlantillasManagerOpen(true); }}
                                        disabled={reporteConfig.campos.length === 0}
                                        sx={{ borderRadius: '4px', textTransform: 'none' }}
                                    >
                                        Guardar plantilla
                                    </Button>
                                    <Box sx={{ flex: 1 }} />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={exportarExcel}
                                        disabled={!puedeAvanzar()}
                                        sx={{ borderRadius: '6px', textTransform: 'none', color: '#2e7d32', borderColor: '#2e7d32' }}
                                    >
                                        Excel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => generarReportePDF(true)}
                                        disabled={!puedeAvanzar()}
                                        sx={{ borderRadius: '6px', textTransform: 'none', color: '#d32f2f', borderColor: '#d32f2f' }}
                                    >
                                        PDF
                                    </Button>
                                </>
                            )}
                        </>
                    )}

                    <Box flexGrow={1} />

                    {pasoActual === 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => setPasoActual(prev => prev + 1)}
                            disabled={!puedeAvanzar()}
                        >
                            Siguiente
                        </Button>
                    )}

                    {pasoActual === 1 && accionSeleccionada !== 'reporte' && (
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => {
                                setPasoActual(2);
                                ejecutarAccionMasiva();
                            }}
                            disabled={!puedeAvanzar()}
                        >
                            Ejecutar
                        </Button>
                    )}

                    {pasoActual === 2 && ejecutando && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={cancelar}
                        >
                            Cancelar Operación
                        </Button>
                    )}

                    {pasoActual === 2 && !ejecutando && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onClose}
                        >
                            Cerrar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {/* Dialog de vista previa PDF */}
            <Dialog
                open={previewOpen}
                onClose={() => {
                    setPreviewOpen(false);
                    if (previewUrl) URL.revokeObjectURL(previewUrl);
                }}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Vista Previa del Reporte</Typography>
                        <IconButton
                            onClick={() => {
                                setPreviewOpen(false);
                                if (previewUrl) URL.revokeObjectURL(previewUrl);
                            }}
                            size="large">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {previewUrl && (
                        <iframe
                            src={previewUrl}
                            style={{ width: '100%', height: '70vh', border: 'none' }}
                            title="Vista previa PDF"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setPreviewOpen(false);
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                    }}>
                        Cerrar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setPreviewOpen(false);
                            if (previewUrl) URL.revokeObjectURL(previewUrl);
                            generarReportePDF(false);
                        }}
                    >
                        Descargar PDF
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Manager de plantillas */}
            <PlantillasReportesManager
                open={plantillasManagerOpen}
                onClose={() => { setPlantillasManagerOpen(false); setPlantillasAbrirEditor(false); }}
                abrirEditor={plantillasAbrirEditor}
                onSelect={aplicarPlantillaGuardada}
                configActual={reporteConfig}
                usuario={usuario}
                permisos={permisos}
                categoriaDefault={categoriaActual}
                camposDisponibles={getCamposDisponibles(tipoExpediente)}
            />
        </>
    );
};

export default AccionesMasivasDialog;